import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doHideSyncModal,
  doShowSyncModal,
} from 'store/actions';
import { Page } from 'components/Page';
import { CardGeneric } from 'components/CardGeneric';
import { str } from 'lib/str';
import { useSelector } from 'hooks/useSelector';
import { selectSyncDownloadCleanData } from 'store/sync/selectors';
import { Button } from 'components/Button';
import { View } from 'react-native';
import { theme } from 'lib/theme';
import { useConfirm } from 'hooks/useConfirm';
import { SyncSummaryCards } from './SyncSummaryCards';
import { PageHeader } from 'components/PageHeader';
import { ScrollView } from 'components/ScrollView';
import { useSafeAreaBottom } from 'hooks/useSafeAreaBottom';
import { Gap } from 'components/Gap';

export const SyncModal: React.FC<ModalScreenProps<ModalScreens.Sync>> = () => {
  const safeAreaBottom = useSafeAreaBottom({ isInModal: true });

  const dispatch = useDispatch();
  const confirm = useConfirm();
  const downloadCleanData = useSelector(selectSyncDownloadCleanData);
  const downloadCleanDataRef = React.useRef(downloadCleanData);
  const [syncFinished, setSyncFinished] = React.useState(false);

  useEsc(() => dispatch(NAVIGATE_MODAL_BACK()));

  const handleSyncFinished = React.useCallback(() => {
    setSyncFinished(true);
  }, []);

  const handleSyncAgainWithoutCache = () => {
    confirm({
      title: 'Sync Again Without Cache',
      text:
        'Sync usually only downloads ***new*** information that has changed in the cloud. ' +
        'Syncing **without cache** will download ***all*** information again.\n\n' +
        'If you think some downloaded information may be out-of-date, try syncing without cache.\n\n' +
        'We recommend only syncing without cache when you have a good internet connection as it may take longer than a normal sync, especially if you have a lot of assets downloaded.',
      confirmText: 'Sync Without Cache',
      destructive: true,
      onConfirm: () => {
        dispatch(doHideSyncModal());
        setTimeout(
          () => dispatch(doShowSyncModal({ downloadCleanData: true })),
          1,
        );
      },
    });
  };

  const showSyncWithoutCache = syncFinished && !downloadCleanDataRef.current;

  return (
    <Page
      header={
        <PageHeader
          title="Sync"
          subtitle={downloadCleanDataRef.current ? 'Without Cache' : ''}
          onPressCancelButton={() => dispatch(NAVIGATE_MODAL_BACK())}
        />
      }
    >
      <Gap style={{ flex: 1 }}>
        <ScrollView withSafeAreaBottom={!showSyncWithoutCache}>
          <Gap>
            <CardGeneric>
              <Txt>
                Changes are saved on your device and automatically synced with
                RiskBase Cloud when you have an internet connection
              </Txt>
              <Txt size="xsmall">
                Pressing the sync button will prompt a manual sync with
                RiskBase. We recommend running a manual sync before starting
                work and again when you{str.apos}re finished to ensure all
                changes and files are uploaded
              </Txt>
            </CardGeneric>
            <SyncSummaryCards
              onTapOption={() =>
                dispatch(NAVIGATE_MODAL_BACK([ModalScreens.Sync]))
              }
              onSyncFinished={handleSyncFinished}
            />
          </Gap>
        </ScrollView>
        {showSyncWithoutCache && (
          <View
            style={{
              padding: theme.padding,
              paddingBottom: safeAreaBottom || theme.padding,
              borderTopWidth: 1,
              borderTopColor: '#dddddd',
            }}
          >
            <Button
              text="Sync Again Without Cache"
              onPress={handleSyncAgainWithoutCache}
              style={{ alignSelf: 'center' }}
            />
          </View>
        )}
      </Gap>
    </Page>
  );
};
